// The workout ENERGY READOUT bundle (HC-8). One derivation of a session's
// energy-view inputs — the read-resolved calories (estimate-vs-measured), the
// measured-value origin, the session HR, and %HRmax — so every surface that
// shows <CaloriesEffortBlock> (mobile logger footer + post-save card, web-rnw
// session detail) computes it ONCE here instead of hand-assembling the same
// five props. Pure, IO-free.
//
// The bundle's field names line up 1:1 with CaloriesEffortBlockProps (minus
// theme/showSources), so a caller spreads it straight in: `{...readout}`.
//
// Age is passed in (via `ageFromBucket` at the callsite) — Profile stores only
// an AgeBucket, and keeping the source out of here mirrors hrEffort.ts's
// age-source-agnostic shape.

import { percentHrMax } from "./hrEffort.js";
import {
  resolveWorkoutCalories,
  type ResolvedWorkoutCalories,
} from "./resolveWorkoutCalories.js";
import type { CaloriesEstimate } from "./schemas/session.js";

export type WorkoutEnergyReadout = {
  /** Read-resolved canonical calories (null when the session carries none). */
  resolved: ResolvedWorkoutCalories | null;
  /** HC dataOrigin of the measured value — resolves its source name. */
  externalOrigin?: string;
  avgHrBpm?: number;
  peakHrBpm?: number;
  /** avg-HR as %HRmax, or null when HR/age missing. */
  hrMaxPct: number | null;
  /** LG-46 — the live count includes a set the athlete hasn't done yet, so the
   *  reading is a projection of the whole session, not a measurement of work
   *  already logged. Only the live path sets it; a saved session leaves it off.
   *  The (i) provenance uses it to declare the projection (the number itself
   *  reads clean, per the no-inline-source-label rule). */
  projected?: boolean;
};

/** The energy-bearing fields a session (or an assembled Workout carrying them
 *  read-only) exposes. */
export type EnergyBearingSession = {
  calories?: number;
  caloriesEstimate?: CaloriesEstimate;
  avgHrBpm?: number;
  peakHrBpm?: number;
  externalOrigin?: string;
};

/** Derive the full energy readout from a session's stored fields. `hcWins` is
 *  the caller's preference (default-ON on the client toggle; web pins ON);
 *  `ageYears` comes from `ageFromBucket(profile.ageBucket)`. */
export function sessionEnergyReadout(
  session: EnergyBearingSession,
  opts: { hcWins: boolean; ageYears: number | undefined },
): WorkoutEnergyReadout {
  return {
    resolved: resolveWorkoutCalories(session, { hcWins: opts.hcWins }),
    externalOrigin: session.externalOrigin,
    avgHrBpm: session.avgHrBpm,
    peakHrBpm: session.peakHrBpm,
    hrMaxPct: percentHrMax(session.avgHrBpm, opts.ageYears),
  };
}
